import React, { type Dispatch, type ReactElement, type ReactNode, type SetStateAction } from 'react';

import { type IconDefinition } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import {
    SectionBlock,
    SectionBlockBody,
    SectionBlockHeader,
    SectionBlockTitle,
} from 'src/registry/components/blocks/SectionBlock';
import { Button } from 'src/registry/ui/Button';
import { Separator } from 'src/registry/ui/Separator';
import { Tooltip, TooltipContent, TooltipTrigger } from 'src/registry/ui/Tooltip';
import { cn } from 'src/utils';

type DetailsSectionProps = {
    title?: string;
    data: { title: string; value: ReactNode }[];
    titleBadge?: ReactElement;
    button?: {
        onClick: Dispatch<SetStateAction<boolean>>;
        title: string;
        icon: IconDefinition;
    };
    isSubSection?: boolean;
    className?: string;
};

function DetailsSection({ title, data, titleBadge, button, isSubSection, className }: DetailsSectionProps) {
    const refs = React.useRef<Record<string, HTMLDivElement | null>>({});
    const [truncatedKeys, setTruncatedKeys] = React.useState<Record<string, boolean>>({});

    React.useEffect(() => {
        const newTruncation: Record<string, boolean> = {};

        data.map(({ title }) => {
            const element = refs.current[title];
            if (!element) return;
            newTruncation[title] = element.scrollWidth > element.clientWidth;
        });

        setTruncatedKeys(newTruncation);
    }, [data]);

    const TooltipElement = ({ title, value }: { title: string; value: string | ReactNode }) => {
        return (
            <Tooltip>
                <TooltipTrigger asChild>
                    <div
                        className="block w-full cursor-default truncate"
                        ref={(el) => {
                            refs.current[title] = el;
                        }}
                    >
                        {value || '-'}
                    </div>
                </TooltipTrigger>
                {truncatedKeys[title] && (
                    <TooltipContent
                        className="hidden h-auto max-w-xs break-words whitespace-normal sm:block"
                        side="right"
                    >
                        <p>{value}</p>
                    </TooltipContent>
                )}
            </Tooltip>
        );
    };

    return (
        <SectionBlock className="flex w-full flex-col gap-2 sm:gap-2">
            {!isSubSection && (
                <>
                    <SectionBlockHeader>
                        <SectionBlockTitle
                            className={cn('flex w-full items-center justify-between whitespace-nowrap', className)}
                        >
                            <div className="flex items-center gap-2">
                                <div>{title || '\u00A0'}</div>
                                {titleBadge && <div>{titleBadge}</div>}
                            </div>
                            <div className="flex justify-end">
                                {button ? (
                                    <Button
                                        variant="outline"
                                        size="xsIcon"
                                        onClick={() => button.onClick((prev) => !prev)}
                                    >
                                        <FontAwesomeIcon icon={button.icon} />
                                        <span className="hidden md:block">{button.title}</span>
                                    </Button>
                                ) : (
                                    <div className="h-10 w-full" />
                                )}
                            </div>
                        </SectionBlockTitle>
                    </SectionBlockHeader>
                    <Separator />
                </>
            )}
            <SectionBlockBody className="flex flex-col gap-2 sm:gap-2">
                {data.map(({ title, value }) => {
                    const dataValue = value ?? '-';
                    const valueDisplay = React.isValidElement(dataValue) ? dataValue : String(dataValue);
                    return (
                        <div key={title}>
                            <div className="mb-1 flex flex-col gap-x-4 sm:mb-2 lg:flex-row lg:items-start">
                                <div className="font-medium break-words text-gray-500 lg:w-1/2 lg:break-normal">
                                    {title}
                                </div>
                                <div className="break-words lg:w-1/2 lg:max-w-full lg:truncate lg:break-normal">
                                    <TooltipElement title={title} value={valueDisplay} />
                                </div>
                            </div>
                            <Separator />
                        </div>
                    );
                })}
            </SectionBlockBody>
        </SectionBlock>
    );
}

export default DetailsSection;
